import { useRef, useState, useEffect } from 'react';
import CallInclusionsForm from './CallInclusionsForm';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import axios from 'axios';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import { Box, Button, IconButton, Tooltip, HStack } from '@chakra-ui/react';
import { BsThreeDotsVertical } from 'react-icons/bs';

const CallInclusions = ({ child }) => {
    const tableRef = useRef();
    const [action, setAction] = useState('GET'); // GET | ADD | EDIT
    const [editData, setEditData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [availableRates, setAvailableRates] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope } = useSelector((state) => state.login);

    // Fetch available rates for appliesTo dropdown
    useEffect(() => {
        if (child?.id && (action === 'ADD' || action === 'EDIT')) {
            axios
                .get(`/CallInclusion/AvailableRates/${child.id}`, {
                    headers: { 'X-RequestScope': requiredScope },
                })
                .then((res) =>
                    setAvailableRates(Array.isArray(res.data) ? res.data : []),
                )
                .catch(() => setAvailableRates([]));
        }
    }, [child?.id, action, requiredScope]);

    // Handlers
    const handleCreate = () => {
        setAction('ADD');
        setEditData(null);
    };
    const handleEdit = (row) => {
        setAction('EDIT');
        setEditData(row);
    };
    const handleCancel = () => {
        setAction('GET');
        setEditData(null);
    };
    const handleDelete = async (row) => {
        console.log('Deleting row:', row);
        setIsDeleting(true);
        try {
            await axios.put('/callinclusion/', {
                ...row,
                id: row.id,
                isActive: false,
            });
            setEditData(null);
            refetch();
            setAction('GET');
            setIsDeleting(false);
        } catch (err) {
            alert('Failed to delete call inclusion.');
        }
    };
    const handleSave = async (values) => {
        // Remove keys with empty string, null, or undefined values
        let filteredValues = Object.fromEntries(
            Object.entries(values).filter(
                ([, v]) => v !== '' && v !== null && v !== undefined,
            ),
        );
        // appliesTo: extract value if array of objects, else keep as is
        if (Array.isArray(filteredValues.appliesTo)) {
            filteredValues.appliesTo = filteredValues.appliesTo.map((opt) =>
                typeof opt === 'object' && opt !== null && 'value' in opt
                    ? opt.value
                    : opt,
            );
        }

        setIsSaving(true);
        try {
            if (action === 'EDIT' && values.id) {
                await axios.put(
                    '/callinclusion/',
                    {
                        ...filteredValues,
                        id: values.id,
                        isActive: true,
                    },
                    { headers: { 'X-RequestScope': requiredScope } },
                );
            } else {
                await axios.post(
                    '/callinclusion/',
                    {
                        ...filteredValues,
                        isActive: true,
                        planID: child?.id || null,
                    },
                    { headers: { 'X-RequestScope': requiredScope } },
                );
            }
            setAction('GET');
            setEditData(null);
            refetch();
        } catch (err) {
            alert('Failed to save call inclusion.');
        } finally {
            setIsSaving(false);
        }
    };

    // Fetch inclusions data using TanStack Query
    const {
        data: inclusionsData = [],
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: ['callInclusions', child?.id],
        queryFn: () => fetchInclusions(child?.id),
        enabled: !!child?.id,
    });

    // AGGrid columns
    const columns = [
        { headerName: 'Name', field: 'name', flex: 1 },
        {
            headerName: 'Priority',
            field: 'priority',
            flex: 1,
            sort: 'asc',
            sortIndex: 0,
        },
        { headerName: 'Included Minutes', field: 'inclusionAmount', flex: 1 },
        {
            headerName: 'Included Minutes Per',
            field: 'inclusionType',
            flex: 1,
            valueGetter: (params) => {
                const inclusionTypeMap = {
                    0: 'Fixed',
                    1: 'User',
                    2: 'Channel',
                    3: 'Number',
                };
                return (
                    inclusionTypeMap[params.data?.inclusionType] ||
                    params.data?.inclusionType
                );
            },
            filterParams: {
                valueGetter: (params) => {
                    const inclusionTypeMap = {
                        0: 'Fixed',
                        1: 'User',
                        2: 'Channel',
                        3: 'Number',
                    };
                    return (
                        inclusionTypeMap[params.data?.inclusionType] ||
                        params.data?.inclusionType
                    );
                },
            },
        },
        {
            headerName: 'Action',
            lockPosition: 'right',
            width: 120,
            sortable: false,
            filter: false,
            suppressMovable: true,
            lockVisible: true,
            suppressColumnsToolPanel: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            enableRowGroup: false,
            resizable: false,
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
            }),
            cellRenderer: (params) => (
                <Tooltip label="Edit">
                    <IconButton
                        icon={
                            <BsThreeDotsVertical
                                fill={darkMode ? 'white' : 'black'}
                            />
                        }
                        aria-label="Edit"
                        variant="ghost"
                        onClick={() => handleEdit(params.data)}
                        _hover={{
                            bg: darkMode ? 'whiteAlpha.200' : 'blackAlpha.100',
                        }}
                        _active={{
                            bg: darkMode ? 'whiteAlpha.300' : 'blackAlpha.200',
                        }}
                        _focus={{ boxShadow: 'none' }}
                    />
                </Tooltip>
            ),
        },
    ];

    // Render logic
    if (action !== 'GET') {
        const defaultInitialValues = {
            name: '',
            priority: '',
            inclusionAmount: '',
            inclusionType: '',
            appliesTo: [],
            planID: child?.id || null,
        };
        return (
            <CallInclusionsForm
                mode={action.toLowerCase()}
                initialValues={editData || defaultInitialValues}
                isLoading={isSaving}
                onBack={handleCancel}
                onSubmit={handleSave}
                isDeleting={isDeleting}
                onDelete={() => handleDelete(editData)}
                darkMode={darkMode}
                availableRates={availableRates}
            />
        );
    }
    return (
        <Box style={{ minHeight: '400px', overflowY: 'auto' }}>
            <AGGridMain
                gridRef={tableRef}
                tableData={{ columns, data: inclusionsData }}
                refetch={refetch}
                isLoading={isLoading}
                titleName="Call Inclusions"
                extraComponents={
                    <HStack spacing={2} paddingLeft={5}>
                        <Tooltip
                            label="Add Call Inclusion"
                            placement="bottom"
                            hasArrow
                            portalProps={{ containerRef: undefined }}>
                            <div
                                style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                }}>
                                <Button
                                    size="sm"
                                    width="160px"
                                    onClick={handleCreate}>
                                    + Add Call Inclusion
                                </Button>
                            </div>
                        </Tooltip>
                    </HStack>
                }
            />
            {error && (
                <Box color="red.500">Error loading call inclusions data.</Box>
            )}
        </Box>
    );
};

const fetchInclusions = async (planId) => {
    if (!planId) return [];
    const response = await axios.get(`/callinclusions/${planId}`);
    return response.data;
};

export default CallInclusions;
